import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';
import { validate, emailRecipientSchema } from '../utils/validators';
import subscriptionService from './subscriptionService';
import encryptionService from './encryptionService';

/**
 * Service layer for doctor summary exports
 * Handles email generation and sending
 */

export const exportService = {
  /**
   * Send doctor summary email
   * @param {string} recipientEmail - Doctor's email
   * @param {object} summaryData - Summary data
   * @param {object} dateRange - Date range for report
   * @returns {Promise<{data, error}>}
   */
  async sendDoctorSummary(recipientEmail, summaryData, dateRange) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    // Check feature access for exports
    const access = await subscriptionService?.checkFeatureAccess('monthly_exports');
    if (!access?.hasAccess) {
      return { 
        data: null, 
        error: { 
          message: access?.reason,
          code: 'FEATURE_LIMIT_REACHED',
          limit: access?.limit,
          used: access?.used
        } 
      };
    }
    
    // Validate email
    const validation = validate(emailRecipientSchema, recipientEmail);
    if (!validation?.success) {
      logger?.warn('Invalid recipient email', { requestId });
      return { data: null, error: { message: 'Invalid email address' } };
    }
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      // Get auth token for edge function
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        return { data: null, error: { message: 'No active session' } };
      }
      
      // Call edge function
      const { data, error } = await supabase?.functions?.invoke('send-doctor-summary', {
        body: {
          recipientEmail,
          summaryData,
          dateRange
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });
      
      if (error) {
        logger?.error('Failed to send doctor summary', error, { requestId, userId: user?.id });
        return { data: null, error };
      }
      
      // Increment usage count after successful export
      await subscriptionService?.incrementFeatureUsage('monthly_exports');
      
      logger?.audit('DOCTOR_SUMMARY_SENT', user?.id, { 
        requestId, 
        recipientEmail,
        dateRange 
      });
      
      return { data, error: null };
    } catch (error) {
      logger?.error('Doctor summary send error', error, { requestId });
      return { data: null, error: { message: 'Failed to send summary' } };
    }
  },

  /**
   * Create encrypted export archive
   * @param {object} exportData - Data to export
   * @param {string} password - Archive password
   * @param {string} filename - Base filename
   * @returns {Promise<{data, error}>}
   */
  async createEncryptedExport(exportData, password, filename = 'health-data-export') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    // Check feature access for exports
    const access = await subscriptionService?.checkFeatureAccess('monthly_exports');
    if (!access?.hasAccess) {
      return { 
        data: null, 
        error: { 
          message: access?.reason,
          code: 'FEATURE_LIMIT_REACHED',
          limit: access?.limit,
          used: access?.used
        } 
      };
    }

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Create encrypted archive
      const { data: archiveData, error: encryptError } = await encryptionService?.createEncryptedArchive(
        exportData,
        password,
        filename
      );

      if (encryptError) {
        logger?.error('Failed to create encrypted archive', encryptError, { requestId });
        return { data: null, error: { message: encryptError } };
      }

      // Increment usage count after successful export
      await subscriptionService?.incrementFeatureUsage('monthly_exports');

      logger?.audit('ENCRYPTED_EXPORT_CREATED', user?.id, { 
        requestId, 
        filename,
        dateRange 
      });

      return { data: archiveData, error: null };
    } catch (error) {
      logger?.error('Encrypted export creation error', error, { requestId });
      return { data: null, error: { message: 'Failed to create encrypted export' } };
    }
  },

  /**
   * Decrypt export archive
   * @param {string} encrypted - Encrypted archive data
   * @param {object} metadata - Archive metadata
   * @param {string} password - Archive password
   * @returns {Promise<{data, error}>}
   */
  async decryptExport(encrypted, metadata, password) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: exportData, error: decryptError } = await encryptionService?.decryptArchive(
        encrypted,
        metadata,
        password
      );

      if (decryptError) {
        logger?.error('Failed to decrypt archive', decryptError, { requestId });
        return { data: null, error: { message: decryptError } };
      }

      logger?.audit('ENCRYPTED_EXPORT_DECRYPTED', null, { requestId });

      return { data: exportData, error: null };
    } catch (error) {
      logger?.error('Export decryption error', error, { requestId });
      return { data: null, error: { message: 'Failed to decrypt export' } };
    }
  },

  /**
   * Export all user data (symptom entries, profile, settings, check-ins) as JSON
   * @returns {Promise<{data, error}>}
   */
  async exportAllDataAsJSON() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Fetch user profile
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (profileError) {
        logger?.error('Failed to fetch user profile', profileError, { requestId });
      }

      // Fetch symptom entries
      const { data: symptomEntries, error: symptomError } = await supabase?.from('symptom_entries')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (symptomError) {
        logger?.error('Failed to fetch symptom entries', symptomError, { requestId });
      }

      // Fetch user settings
      const { data: settings, error: settingsError } = await supabase?.from('user_settings')?.select('*')?.eq('user_id', user?.id)?.single();

      if (settingsError) {
        logger?.error('Failed to fetch user settings', settingsError, { requestId });
      }

      // Fetch managed profiles (dependents)
      const { data: managedProfiles, error: managedError } = await supabase?.from('managed_profiles')?.select('*')?.eq('caregiver_id', user?.id);

      if (managedError) {
        logger?.error('Failed to fetch managed profiles', managedError, { requestId });
      }

      // Fetch subscription info
      const { data: subscription, error: subError } = await supabase?.from('user_subscriptions')?.select('*, subscription_plans(*)')?.eq('user_id', user?.id)?.single();

      if (subError) {
        logger?.error('Failed to fetch subscription', subError, { requestId });
      }

      // Compile all data
      const exportData = {
        exportDate: new Date()?.toISOString(),
        exportVersion: '1.0.0',
        user: {
          id: user?.id,
          email: user?.email,
          emailVerified: user?.email_confirmed_at ? true : false,
          createdAt: user?.created_at
        },
        profile: profile || null,
        symptomEntries: symptomEntries || [],
        settings: settings || null,
        managedProfiles: managedProfiles || [],
        subscription: subscription || null,
        statistics: {
          totalSymptomEntries: symptomEntries?.length || 0,
          totalManagedProfiles: managedProfiles?.length || 0,
          accountAge: user?.created_at ? Math.floor((Date.now() - new Date(user.created_at)?.getTime()) / (1000 * 60 * 60 * 24)) : 0
        }
      };

      logger?.audit('DATA_EXPORTED_JSON', user?.id, { requestId, recordCount: symptomEntries?.length || 0 });

      return { data: exportData, error: null };
    } catch (error) {
      logger?.error('JSON export error', error, { requestId });
      return { data: null, error: { message: 'Failed to export data as JSON' } };
    }
  },

  /**
   * Export all user data as CSV
   * @returns {Promise<{data, error}>}
   */
  async exportAllDataAsCSV() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Fetch symptom entries
      const { data: symptomEntries, error: symptomError } = await supabase?.from('symptom_entries')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (symptomError) {
        logger?.error('Failed to fetch symptom entries', symptomError, { requestId });
        return { data: null, error: { message: 'Failed to fetch symptom data' } };
      }

      // Convert to CSV format
      if (!symptomEntries || symptomEntries?.length === 0) {
        return { data: 'No symptom data available', error: null };
      }

      // CSV Headers
      const headers = [
        'Date',
        'Time',
        'Category',
        'Symptoms',
        'Severity',
        'Severity Numeric',
        'Feeling Level',
        'Body Locations',
        'Daily Impacts',
        'Triggers',
        'Medication Status',
        'Notes',
        'Duration (hours)',
        'Created At'
      ];

      // CSV Rows
      const rows = symptomEntries?.map(entry => [
        entry?.symptom_date || '',
        entry?.symptom_time || '',
        entry?.category || '',
        Array.isArray(entry?.symptoms) ? entry?.symptoms?.join('; ') : '',
        entry?.severity || '',
        entry?.severity_numeric || '',
        entry?.feeling_level || '',
        Array.isArray(entry?.body_locations) ? entry?.body_locations?.join('; ') : '',
        Array.isArray(entry?.daily_impacts) ? entry?.daily_impacts?.join('; ') : '',
        Array.isArray(entry?.triggers) ? entry?.triggers?.join('; ') : '',
        entry?.medication_status || '',
        entry?.notes || '',
        entry?.duration_hours || '',
        entry?.created_at || ''
      ]);

      // Build CSV string
      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => {
          // Escape cells containing commas, quotes, or newlines
          const cellStr = String(cell);
          if (cellStr?.includes(',') || cellStr?.includes('"') || cellStr?.includes('\n')) {
            return `"${cellStr?.replace(/"/g, '""')}`;
          }
          return cellStr;
        })?.join(','))
      ]?.join('\n');

      logger?.audit('DATA_EXPORTED_CSV', user?.id, { requestId, recordCount: symptomEntries?.length || 0 });

      return { data: csvContent, error: null };
    } catch (error) {
      logger?.error('CSV export error', error, { requestId });
      return { data: null, error: { message: 'Failed to export data as CSV' } };
    }
  }
};

export default exportService;